// The dashboard config as leshabank's API stores it. Since 37f2a2b its draft and published configs are validated on
// every save (services/api dashboard_widget_config.validate_widget_config) and a value it doesn't know fails the whole
// save with a 422. The canvas keeps its own shapes in memory; the builder sends `toWireConfig` and reads `fromWireConfig`.
//
// - A table column format "percent_change" / "return" (a percent that always shows ▲/▼, backlog #7) is not in its list
//   (auto, text, number, integer, percent). It is sent as "percent" with `signed: true`, which our renderer already
//   reads as a signed percent (isSignedPercentColumn), so a published or agent-read copy still shows ▲/▼; `display`
//   brings the exact format back on load. The validator reads only `format` and `decimals` of an entry.
// - kpi_calculation / kpi_display / kpi_comparison are refused on anything but a KPI. One left behind by a type change
//   is dropped (the canvas no longer shows it).

type Json = Record<string, unknown>

const SIGNED_PERCENT_FORMATS = new Set(["percent_change", "return"])
const KPI_ONLY_FIELDS = ["kpi_calculation", "kpi_display", "kpi_comparison"] as const

const isRecord = (value: unknown): value is Json => typeof value === "object" && value !== null && !Array.isArray(value)

function mapWidgets<T>(config: T, map: (widget: Json) => Json): T {
  if (!isRecord(config)) return config
  const widgets = (list: unknown) => (Array.isArray(list) ? list.map((widget) => (isRecord(widget) ? map(widget) : widget)) : list)
  const next: Json = { ...config }
  if ("widgets" in config) next.widgets = widgets(config.widgets)
  if (Array.isArray(config.tabs)) next.tabs = config.tabs.map((tab) => (isRecord(tab) ? { ...tab, widgets: widgets(tab.widgets) } : tab))
  return next as T
}

function mapColumnFormats(widget: Json, map: (entry: Json) => Json): Json {
  const data = widget.data
  if (widget.type !== "table" || !isRecord(data) || !isRecord(data.columnFormats)) return widget
  const formats = Object.fromEntries(Object.entries(data.columnFormats).map(([column, entry]) => [column, isRecord(entry) ? map(entry) : entry]))
  return { ...widget, data: { ...data, columnFormats: formats } }
}

export function toWireConfig<T>(config: T): T {
  return mapWidgets(config, (widget) => {
    let next = widget
    if (widget.type !== "kpi" && KPI_ONLY_FIELDS.some((field) => widget[field] != null)) {
      next = { ...widget }
      KPI_ONLY_FIELDS.forEach((field) => delete next[field])
    }
    return mapColumnFormats(next, (entry) =>
      typeof entry.format === "string" && SIGNED_PERCENT_FORMATS.has(entry.format)
        ? { ...entry, format: "percent", signed: true, display: entry.format }
        : entry,
    )
  })
}

export function fromWireConfig<T>(config: T): T {
  return mapWidgets(config, (widget) =>
    mapColumnFormats(widget, (entry) => {
      if (entry.format !== "percent" || typeof entry.display !== "string" || !SIGNED_PERCENT_FORMATS.has(entry.display)) return entry
      const { display, signed: _signed, ...rest } = entry
      return { ...rest, format: display }
    }),
  )
}
